// NO IMPORTER URLS IN THIS FILE.
//
// Aether used to hold REACT_APP_IMPORTER_BASE_URL / _SUBSCRIPTION_URL and build
// every importer path from them at module load. That hardcoded one importer per
// deployment: adding FIFA alongside Opta, or moving either host, meant a
// frontend rebuild.
//
// Importer endpoints are now resolved per action from the competition on screen
// — services/profile.service.js `resolveImporter()` asks DWS, which maps
// competition -> profile -> provider -> endpoint out of one Mongo document.
// Every import, subscribe and unsubscribe passes the endpoint it was handed.
//
// If you are about to add an importer URL here, you are re-creating the problem.

// Deliberately NOT a top-level throw.
//
// A throw here happens during MODULE EVALUATION, before React renders anything,
// so no error boundary can catch it and the app is a blank page with a console
// message. One missing env var took out the entire application. Report it and
// let the failure surface at the call site, where it can be caught and shown.
//
// missingConfig is rendered by <ConfigCheck> at the app root.
export const missingConfig = [];

export const BASE_URL = process.env.REACT_APP_API_BASE_URL;
if (!BASE_URL) {
  missingConfig.push("REACT_APP_API_BASE_URL");
  console.error("[config] REACT_APP_API_BASE_URL is not defined — profile and endpoint resolution will fail");
}

export const OPUS_URL = process.env.REACT_APP_OPUS_URL;
// DataImporter_Opta_Mocker's pull-server.js — optional, dev-only. Left
// unguarded (unlike IMPORTER_BASE_URL) so a deployment that hasn't wired it
// up yet doesn't crash the whole app; the mocker-pull call just fails with a
// clear error if it's missing.
export const MOCKER_PULL_URL = process.env.REACT_APP_MOCKER_PULL_URL;
// The mocker's own login is a single static shared credential (not tied to
// Opus/Cognito at all — see DataImporter_Opta_Mocker/src/services/local-auth.js),
// so the Opus session's own accessKey no longer verifies there. This is a
// separate, lower-stakes secret than the rest of this file's URLs, but it's
// still an internal-tool login shipped in the client bundle — acceptable
// only because aether itself already sits behind Opus auth.
export const MOCKER_USERNAME = process.env.REACT_APP_MOCKER_USERNAME;
export const MOCKER_PASSWORD = process.env.REACT_APP_MOCKER_PASSWORD;
export const LEAGUE_TABLE_REFRESH_URL = `${process.env.REACT_APP_OPUS_WORKFLOW_URL}/webhook/league-table-refresh`;

/**
 * Paths ON an importer, appended to the endpoint resolved for the competition.
 *
 * Paths only — never a host. Every importer serves the identical path set
 * regardless of transport (the FIFA importer answers /api/ws/* over Kafka), so
 * picking an importer is choosing a base URL and nothing else. That is the
 * property that keeps provider knowledge out of this frontend; do not add a
 * per-provider path map here.
 */
export const IMPORTER_PATHS = {
  importerUi: "/webhook/importer-ui",
  poolStatus: "/webhook/pool-status",
  activityTracker: "/webhook/activity-tracker",
  squadPullByTeams: "/webhook/squad-pull-by-teams",
  squadsExport: "/api/squads-export",
  subscribe: "/api/ws/subscribe",
  unsubscribe: "/api/ws/unsubscribe",
  subscriptions: "/api/ws/subscriptions",
  status: "/api/ws/status",
};

/** Join a resolved endpoint with one of the paths above. */
export const importerUrl = (endpoint, path) =>
  `${String(endpoint || "").replace(/\/+$/, "")}${path}`;

export const API_URLS = {
  competitions: `${BASE_URL}/webhook/competition`,
  seasons: `${BASE_URL}/webhook/season`,
  fixtures: `${BASE_URL}/api/query`,
  players: `${BASE_URL}/api/query`,
  // subscribe / unsubscribe / subscriptions / status are NOT here: they are
  // importer calls, and the importer is chosen per competition. See
  // IMPORTER_PATHS above and services/fixtures.services.js.
  mockerPull: MOCKER_PULL_URL ? `${MOCKER_PULL_URL}/api/pull` : null,
  createCompetition: `${BASE_URL}/api/competition/create`,
  // Sport list — source for the sport dropdown on competition creation.
  sports: `${BASE_URL}/api/sport`,
  createSport: `${BASE_URL}/api/sport/create`,
  createSeason: `${BASE_URL}/api/season/create`,
  createFixture: `${BASE_URL}/api/fixture`,
  createTeam: `${BASE_URL}/api/team/create`,
  createPlayer: `${BASE_URL}/api/player/create`,
  createStaff: `${BASE_URL}/api/staff/create`,
  createVenue: `${BASE_URL}/api/venue/create`,
  createOfficial: `${BASE_URL}/api/official/create`,
  resetFixture: `${BASE_URL}/api/fixture/reset`,
  saveSeasonScene: `${BASE_URL}/api/setting`,
  getAllScenes: `${BASE_URL}/api/scene/organization`
};

export const entityContextUrl = (entity, id) =>
  `${BASE_URL}/api/${entity}/${encodeURIComponent(id)}/context`;
